import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { auth, db } from "@/src/lib/firebase";
import { doc, getDoc } from "firebase/firestore";
import { Meeting } from "@/src/types";
import { Loader2, Video, PhoneOff, Mic, MicOff, VideoOff, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

declare global {
  interface Window {
    JitsiMeetExternalAPI: any;
  }
}

const MeetingRoom: React.FC = () => {
  const { meetingId } = useParams<{ meetingId: string }>();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [loading, setLoading] = useState(true);
  const jitsiContainerRef = useRef<HTMLDivElement>(null);
  const [jitsiApi, setJitsiApi] = useState<any>(null);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(false);

  useEffect(() => {
    const fetchMeeting = async () => {
      if (!meetingId) return;
      try {
        const meetingSnap = await getDoc(doc(db, "meetings", meetingId));
        if (meetingSnap.exists()) {
          setMeeting({ ...meetingSnap.data(), id: meetingSnap.id } as Meeting);
        } else {
          toast.error("Reunión no encontrada");
          navigate("/dashboard");
        }
      } catch (error) {
        console.error("Error fetching meeting:", error);
        toast.error("Error al cargar la reunión");
      } finally {
        setLoading(false);
      }
    };

    fetchMeeting();
  }, [meetingId, navigate]);

  useEffect(() => {
    if (!meeting || !jitsiContainerRef.current) return;

    const domain = "meet.jit.si";
    const options = {
      roomName: `EmpreMarket-${meeting.id}`,
      width: "100%",
      height: "100%",
      parentNode: jitsiContainerRef.current,
      userInfo: {
        displayName: auth.currentUser?.displayName || "Usuario de EmpreMarket",
        email: auth.currentUser?.email || ""
      },
      configOverwrite: {
        startWithAudioMuted: false,
        startWithVideoMuted: false,
        prejoinPageEnabled: false,
        disableDeepLinking: true
      },
      interfaceConfigOverwrite: {
        TOOLBAR_BUTTONS: [
          'microphone', 'camera', 'closedcaptions', 'desktop', 'fullscreen',
          'fmsort', 'hangup', 'chat', 'recording',
          'livestreaming', 'etherpad', 'sharedvideo', 'settings', 'raisehand',
          'videoquality', 'filmstrip', 'invite', 'feedback', 'stats', 'shortcuts',
          'tileview', 'videobackgroundblur', 'download', 'help', 'mute-everyone',
          'security'
        ],
      }
    };

    const script = document.createElement("script");
    script.src = `https://${domain}/external_api.js`;
    script.async = true;
    script.onload = () => {
      const api = new window.JitsiMeetExternalAPI(domain, options);
      setJitsiApi(api);

      api.addEventListeners({
        videoConferenceJoined: () => {
          console.log("Local User Joined");
        },
        videoConferenceLeft: () => {
          navigate("/dashboard");
        },
        audioMuteStatusChanged: (payload: any) => {
          setIsAudioMuted(payload.muted);
        },
        videoMuteStatusChanged: (payload: any) => {
          setIsVideoMuted(payload.muted);
        }
      });
    };

    document.body.appendChild(script);

    return () => {
      if (jitsiApi) jitsiApi.dispose();
      document.body.removeChild(script);
    };
  }, [meeting, navigate]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background">
        <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground animate-pulse">Preparando sala de reunión...</p>
      </div>
    );
  }

  if (!meeting) return null;

  return (
    <div className="flex flex-col h-screen bg-black overflow-hidden">
      {/* Header */}
      <div className="bg-zinc-900 border-b border-zinc-800 p-4 flex items-center justify-between z-10">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-primary/20 flex items-center justify-center">
            <Video className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-white font-bold leading-none">{meeting.title}</h1>
            <p className="text-zinc-500 text-xs mt-1">Sala de Reunión Interna • EmpreMarket</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="destructive" 
            size="sm" 
            className="gap-2 rounded-full px-4"
            onClick={() => {
              if (jitsiApi) jitsiApi.executeCommand('hangup');
              else navigate("/dashboard");
            }}
          >
            <PhoneOff className="h-4 w-4" />
            Salir
          </Button>
        </div>
      </div>

      {/* Video Container */}
      <div className="flex-1 relative bg-zinc-950">
        <div ref={jitsiContainerRef} className="w-full h-full" />
        
        {!jitsiApi && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
              <p className="text-zinc-400 text-sm">Cargando interfaz de video...</p>
            </div>
          </div>
        )}
      </div>

      {/* Custom Controls Overlay (Optional, Jitsi has its own) */}
      <div className="bg-zinc-900 p-4 flex items-center justify-center gap-4 border-t border-zinc-800">
        <Button 
          variant={isAudioMuted ? "destructive" : "secondary"} 
          size="icon" 
          className="h-12 w-12 rounded-full"
          onClick={() => jitsiApi?.executeCommand('toggleAudio')}
        >
          {isAudioMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
        </Button>
        
        <Button 
          variant={isVideoMuted ? "destructive" : "secondary"} 
          size="icon" 
          className="h-12 w-12 rounded-full"
          onClick={() => jitsiApi?.executeCommand('toggleVideo')}
        >
          {isVideoMuted ? <VideoOff className="h-5 w-5" /> : <Video className="h-5 w-5" />}
        </Button>

        <Button 
          variant="secondary" 
          size="icon" 
          className="h-12 w-12 rounded-full"
          onClick={() => jitsiApi?.executeCommand('toggleChat')}
        >
          <MessageSquare className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
};

export default MeetingRoom;
